// src/components/issues/BoutonEnvoiBE.tsx — SCE Issues
// Icône « Envoyer au BE » en tête de fiche, à côté de la cloche (20/10) :
// même allure que la cloche, le crayon et Rafraîchir (icône seule, 44 px de
// cible). Au survol, une infobulle dit ce qu'elle fait, nomme le BE du métier
// et, s'il y a déjà eu un envoi, quand et par qui. Au téléphone, l'action est
// dans le menu ⋯ de la fiche (MenuActionsFiche), avec son libellé en clair.
//
// Visible des coordinateurs du métier, de leurs suppléants et de l'admin
// (`peutPiloter` de la fiche), pas sur un problème annulé. GRISÉE, avec la
// mention « En cours », tant que l'envoi n'est pas ouvert (envoiBEOuvert, panneau d'administration) :
// `aria-disabled` plutôt que `disabled`, pour que l'infobulle s'affiche quand même.

import type { JSX } from 'react'
import { Send } from 'lucide-react'
import { Infobulle } from '@/components/ui/Infobulle'
import { METIERS_LABELS_COURT } from '@/config/metiers.config'
import type { Metier } from '@/types/issue.types'

function jourMois(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`
}

export type DernierEnvoi = { date: string; auteur: string; destination: string } | null

/** « Envoyer au BE MECA » */
export function titreEnvoiBE(metier: Metier): string {
  return `Envoyer au BE ${METIERS_LABELS_COURT[metier]}`
}

/** « Envoyé au BE MECA le 16/10 par Romuald Ruse », ou null. */
export function texteDernierEnvoi(dernier: DernierEnvoi): string | null {
  return dernier ? `Envoyé ${dernier.destination} le ${jourMois(dernier.date)}${dernier.auteur ? ` par ${dernier.auteur}` : ''}` : null
}

export const DETAIL_ENVOI_BE = 'Teams ou mail · résumé et lien vers la fiche'
export const DETAIL_ENVOI_BE_EN_COURS = 'En cours : bientôt disponible'

export function BoutonEnvoiBE({
  metier,
  dernier,
  onClick,
  desactive,
  enCours,
}: {
  metier: Metier
  /** Le dernier envoi de ce problème (relu dans l'activité), ou null. */
  dernier: DernierEnvoi
  onClick: () => void
  desactive?: boolean
  /** Pas encore ouvert : grisée, l'infobulle dit « En cours ». */
  enCours?: boolean
}): JSX.Element {
  const titre = titreEnvoiBE(metier)
  const deja = texteDernierEnvoi(dernier)
  const inactif = enCours || desactive
  return (
    <Infobulle
      titre={titre}
      detail={enCours ? DETAIL_ENVOI_BE_EN_COURS : DETAIL_ENVOI_BE}
      lignes={deja && !enCours ? [deja] : undefined}
      largeur={280}
    >
      <button
        type="button"
        onClick={() => {
          if (!inactif) onClick()
        }}
        aria-disabled={inactif}
        aria-label={enCours ? `${titre} (en cours)` : deja ? `${titre} — ${deja}` : titre}
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          border: 'none',
          background: 'transparent',
          color: enCours ? 'var(--color-gray-50)' : 'var(--color-black)',
          cursor: inactif ? 'default' : 'pointer',
        }}
      >
        <Send size={18} />
      </button>
    </Infobulle>
  )
}
